const checkInStoreSelectors = {
  drawerContent: '[data-check-in-store-content]',
  drawerAccordion: '.custom-accordion',
  drawerAccordionTrigger: '.custom-accordion__trigger',
  drawerAccordionPanel: '.custom-accordion__panel'
}

class CheckInStoreDrawer extends BaseDrawer {
  constructor() {
    const globalSelectors = {
      triggerButton: '[data-check-in-store-button]',
      drawer: '[data-check-in-store-drawer]',
      overlay: '[data-check-in-store-drawer-overlay]'
    };
    super(globalSelectors);

    this.init();
  }

  init() {
    this.productData = [];
    this.rootUrl = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;
    this.currentVariantId = this.dataset.variantId;

    this.fetchAvailability(this.currentVariantId);

    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleOptionValueChange.bind(this)
    );

    document.addEventListener('pss:coords-changed', () => this.enrichStores(this.currentVariantId));

    // BUG-06: event delegation so handler survives DOM re-renders
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cis-set-store]');
      if (!btn || btn.disabled) return;
      const item = btn.closest('.store-availability-list__item');
      if (item) this.setPreferredStore(item);
    });
  }

  async fetchAvailability(variantId) {
    if (!variantId) return;

    try {
      const inventoryData = await this.fetchInventoryData(this.rootUrl, variantId);
      this.renderPreview(inventoryData);
      await this.enrichStores(variantId);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  }

  async fetchInventoryData(rootUrl, variantId) {
    const response = await fetch(`${rootUrl}variants/${variantId}/?section_id=check-in-store`);
    if (!response.ok) throw new Error('Variant section fetch failed');

    const text = await response.text();
    const sectionInnerHTML = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('.shopify-section');

    if (!sectionInnerHTML) throw new Error('Section HTML not found');
    return sectionInnerHTML;
  }

  renderPreview(sectionInnerHTML) {
    const content = document.querySelector(checkInStoreSelectors.drawerContent);
    content.innerHTML = sectionInnerHTML.outerHTML;
    this.initAccordions();
  }

  async enrichStores(variantId) {
    const token = window.pss?.storefrontToken;
    if (!token || !variantId) return;

    let stores = [];
    try {
      stores = await this.fetchStoreAvailability(token, variantId);
    } catch {
      return;
    }

    const rawCoords = sessionStorage.getItem('customerCoords');
    const userCoords = rawCoords ? JSON.parse(rawCoords) : null;
    const ccDistKm = window.pss?.ccDistanceKm ?? 30;
    const preferredStore = localStorage.getItem('cisPreferredStore');

    const items = Array.from(
      document.querySelectorAll('[data-store-availability-drawer-content] .store-availability-list__item')
    );

    items.forEach((item) => {
      const match = stores.find((s) => s.location.name === item.dataset.locationName);
      if (!match) return;

      item.dataset.inStock = match.available ? '1' : '0';

      delete item.dataset.distanceKm;
      const distEl = item.querySelector('.cis__distance');
      if (distEl) { distEl.textContent = ''; distEl.hidden = true; }

      let km = null;
      if (userCoords) {
        const { latitude, longitude } = match.location.address;
        if (latitude != null && longitude != null) {
          km = this.haversineKm(userCoords.lat, userCoords.lng, latitude, longitude);
          item.dataset.distanceKm = km;
          if (distEl) {
            distEl.textContent = `${km.toFixed(1)}KM`;
            distEl.hidden = false;
          }
        }
      }

      // BUG-04 + BUG-05: C&C green when in stock + within range, grey (unavailable) otherwise
      const badge = item.querySelector('[data-cis-click-collect]');
      if (badge) {
        const withinRange = km === null || km <= ccDistKm;
        const ccAvailable = match.pickUpTime && match.available && withinRange;
        badge.classList.toggle('cis__indicator--oos', !ccAvailable);
      }

      // BUG-06: reflect saved preferred store state
      const setStoreBtn = item.querySelector('[data-cis-set-store]');
      if (setStoreBtn) {
        if (item.dataset.locationName === preferredStore) {
          setStoreBtn.textContent = 'Your Store';
          setStoreBtn.disabled = true;
        } else {
          setStoreBtn.textContent = 'Set as your store';
          setStoreBtn.disabled = false;
        }
      }
    });

    this.sortStores(items);
  }

  // BUG-06: persist preferred store and update all button states
  setPreferredStore(selectedItem) {
    const storeName = selectedItem.dataset.locationName;
    localStorage.setItem('cisPreferredStore', storeName);

    const allItems = Array.from(
      document.querySelectorAll('[data-store-availability-drawer-content] .store-availability-list__item')
    );
    allItems.forEach((item) => {
      const btn = item.querySelector('[data-cis-set-store]');
      if (!btn) return;
      if (item.dataset.locationName === storeName) {
        btn.textContent = 'Your Store';
        btn.disabled = true;
        btn.hidden = false;
      } else {
        btn.textContent = 'Set as your store';
        btn.disabled = false;
      }
    });

    this.sortStores(allItems);
  }

  sortStores(items) {
    const list = items[0]?.parentElement;
    if (!list) return;

    const preferredStore = localStorage.getItem('cisPreferredStore');
    const ccDistKm = window.pss?.ccDistanceKm ?? 30;

    const storeGroup = (item) => {
      if (item.dataset.locationName === preferredStore) return 0;
      const inStock = item.dataset.inStock === '1';
      const dist = parseFloat(item.dataset.distanceKm);
      const withinRange = isNaN(dist) || dist <= ccDistKm;
      const isOutlet = (item.dataset.locationName || '').toLowerCase().includes('surplus');
      // Group 1: in stock + within 30km (non-outlet before outlet)
      if (inStock && withinRange) return isOutlet ? 2 : 1;
      // Group 2: in stock + beyond 30km
      if (inStock) return isOutlet ? 4 : 3;
      // Group 3: out of stock
      return isOutlet ? 6 : 5;
    };

    const sorted = [...items].sort((a, b) => {
      const ga = storeGroup(a);
      const gb = storeGroup(b);
      if (ga !== gb) return ga - gb;
      // Within same group, sort by distance
      const da = parseFloat(a.dataset.distanceKm) || Infinity;
      const db = parseFloat(b.dataset.distanceKm) || Infinity;
      return da - db;
    });

    const preferred = preferredStore && sorted[0]?.dataset.locationName === preferredStore ? sorted[0] : null;
    const others = preferred ? sorted.slice(1) : sorted;

    // Remove existing section labels and rebuild list
    list.querySelectorAll('.cis__section-label').forEach((el) => el.remove());
    items.forEach((item) => item.classList.remove('cis__preferred-card'));

    list.innerHTML = '';

    if (preferred) {
      const prefLabel = document.createElement('li');
      prefLabel.className = 'cis__section-label';
      prefLabel.setAttribute('aria-hidden', 'true');
      prefLabel.textContent = 'Preferred Store';
      list.appendChild(prefLabel);
      preferred.classList.add('cis__preferred-card');
      list.appendChild(preferred);
    }

    if (others.length) {
      if (preferred) {
        const othersLabel = document.createElement('li');
        othersLabel.className = 'cis__section-label cis__section-label--others';
        othersLabel.setAttribute('aria-hidden', 'true');
        othersLabel.textContent = 'Other Stores';
        list.appendChild(othersLabel);
      }
      others.forEach((item) => list.appendChild(item));
    }

  }

  async fetchStoreAvailability(token, variantId) {
    const gid = `gid://shopify/ProductVariant/${variantId}`;
    const query = `{
      node(id: "${gid}") {
        ... on ProductVariant {
          storeAvailability(first: 20) {
            edges {
              node {
                available
                pickUpTime
                location { name address { formatted latitude longitude } }
              }
            }
          }
        }
      }
    }`;

    const res = await fetch('/api/2026-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });
    const data = await res.json();
    return data?.data?.node?.storeAvailability?.edges?.map((e) => e.node) ?? [];
  }

  haversineKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  handleOptionValueChange(event) {
    this.currentVariantId = event.data.variant.id;
    this.fetchAvailability(this.currentVariantId);
  }
}

customElements.define('check-in-store-drawer', CheckInStoreDrawer);
